import React, { useState } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import { Public, Lock } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { getCrossOrigin } from '../utils/imageUtils';
import { NoImagePlaceholder } from './NoImagePlaceholder';

export interface ImageGalleryImage {
  imageId: string;
  url: string;
  thumbnailUrl?: string;
  visibility: 'public' | 'private';
  filename?: string;
  // Internal-only per-image upload timestamp (issue #1112). Rendered under
  // the main image when the viewer is the owner (canEdit=true). The backend
  // scrubs this field on public responses, so for non-owners it arrives
  // null and nothing is rendered.
  uploadedAt?: string | null;
}

export interface ImageGalleryProps {
  images: ImageGalleryImage[];
  title: string;
  canEdit: boolean;
  onToggleVisibility?: (imageId: string) => void;
}

const ImageGallery: React.FC<ImageGalleryProps> = ({
  images,
  title,
  canEdit,
  onToggleVisibility,
}) => {
  const theme = useTheme();
  const [selectedIndex, setSelectedIndex] = useState(0);

  const visibleImages = canEdit
    ? images
    : images.filter((img) => img.visibility === 'public');

  if (visibleImages.length === 0) {
    return (
      <Box sx={{ width: '100%', height: { xs: 350, md: 500 } }}>
        <NoImagePlaceholder variant="detail" />
      </Box>
    );
  }

  // Clamp index to prevent out-of-bounds when filtering removes the selected image
  const clampedIndex = Math.min(selectedIndex, visibleImages.length - 1);
  const selectedImage = visibleImages[clampedIndex];
  const showThumbnails = visibleImages.length > 1;

  const handleThumbnailClick = (index: number) => {
    setSelectedIndex(index);
  };

  const handleToggleVisibility = (
    event: React.MouseEvent,
    imageId: string
  ) => {
    event.stopPropagation();
    if (onToggleVisibility) {
      onToggleVisibility(imageId);
    }
  };

  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 1, md: 1 } }}>
      {/* Main Image Display */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <Box
          sx={{
            height: { xs: 350, md: 500 },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: theme.palette.grey[100],
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 1,
            overflow: 'hidden',
          }}
        >
          <img
            src={selectedImage.url}
            alt={title}
            crossOrigin={getCrossOrigin(selectedImage.url)}
            style={{
              maxWidth: '100%',
              maxHeight: '100%',
              objectFit: 'contain',
            }}
          />
        </Box>
      </Box>

      {/* Vertical Thumbnail Column (horizontal on mobile) */}
      {showThumbnails && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'row', md: 'column' },
            gap: 0.75,
            overflowX: { xs: 'auto', md: 'visible' },
            overflowY: { xs: 'visible', md: 'auto' },
            maxHeight: { md: 500 },
            pb: { xs: 1, md: 0 },
            ml: { md: 1 },
            width: { md: 96 },
            flexShrink: 0,
          }}
        >
          {visibleImages.map((image, index) => {
            const isSelected = index === clampedIndex;
            const thumbnailSrc = image.thumbnailUrl || image.url;

            return (
              <Box
                key={image.imageId}
                role="button"
                onClick={() => handleThumbnailClick(index)}
                sx={{
                  position: 'relative',
                  flexShrink: 0,
                  cursor: 'pointer',
                  '&:hover': {
                    opacity: 0.8,
                  },
                }}
              >
                {/* Thumbnail Image Container */}
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: theme.palette.grey[100],
                    border: isSelected
                      ? `3px solid ${theme.palette.primary.main}`
                      : `1px solid ${theme.palette.divider}`,
                    borderRadius: 1,
                    overflow: 'hidden',
                  }}
                >
                  <img
                    src={thumbnailSrc}
                    alt={`${title} thumbnail ${index + 1}`}
                    crossOrigin={getCrossOrigin(thumbnailSrc)}
                    style={{
                      maxWidth: '100%',
                      maxHeight: '100%',
                      objectFit: 'contain',
                    }}
                  />
                </Box>

                {/* Visibility Toggle Icon */}
                {canEdit && onToggleVisibility && (
                  <IconButton
                    data-testid={`visibility-toggle-${image.imageId}`}
                    onClick={(e) => handleToggleVisibility(e, image.imageId)}
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: 4,
                      right: 4,
                      backgroundColor: 'rgba(255, 255, 255, 0.8)',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.95)',
                      },
                    }}
                  >
                    {image.visibility === 'public' ? (
                      <Public fontSize="small" />
                    ) : (
                      <Lock fontSize="small" />
                    )}
                  </IconButton>
                )}
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default ImageGallery;
